/**
 * Moored market services (M10 P5a8a): one batched tender run per visit.
 *
 * While a hull is moored at a ring (moored = docked at the ring, `moorFlow.ts`) and `moor.json tender.enabled` is on,
 * market buys and sells don't settle at once: each queues a line here ("Tender: n t queued"). **Send tender** moves
 * every queued line into one run that takes `tenderS + tenderSPerKt × t / 1000` seconds, capped at `tenderCapS`;
 * when it lands the caller settles the run's lines (`takeDelivered`). Leaving the ring cancels the unsent lines
 * (`leaveRing`), but a sent run keeps its timer through a launch (the cradle S may launch while it runs).
 *
 * `tenderGate` is the one switch: false (the key off, or docked in a bay) → today's instant trade, untouched.
 * Queueing and sending are cold (one call per click); `stepTender` allocates nothing. The sim holds while docked, so
 * the career runtime steps a sent run on a wall-clock interval of `tender.tickS` (a cold timer, never the frame loop).
 */
import type { TenderConfig } from '../docking/moorConfig'

export const TENDER_PHASE = { idle: 0, running: 1, landed: 2 } as const
export type TenderPhase = (typeof TENDER_PHASE)[keyof typeof TENDER_PHASE]

/** One queued market line: buy or sell `tonnes` of `item` (a ware id). */
export interface TenderLine {
  readonly side: 'buy' | 'sell'
  readonly item: string
  readonly tonnes: number
}

/** The player's tender side state (one per career; mutated in place). */
export interface TenderState {
  phase: TenderPhase
  /** The station the queued lines are for (-1: none). */
  station: number
  /** The station the sent run is for (-1: none). */
  runStation: number
  /** Queued, unsent lines. */
  queued: TenderLine[]
  /** The lines in the sent run. */
  sent: TenderLine[]
  /** Tonnes queued. */
  queuedT: number
  /** Tonnes in the sent run. */
  sentT: number
  /** Seconds the run has flown. */
  elapsedS: number
  /** The run's length (s). */
  durationS: number
  /** Runs sent so far (one per visit when every line rides one batch). */
  runs: number
}

export function createTenderState(): TenderState {
  return { phase: TENDER_PHASE.idle, station: -1, runStation: -1, queued: [], sent: [], queuedT: 0, sentT: 0, elapsedS: 0, durationS: 0, runs: 0 }
}

/** Whether a market trade goes through the tender: the key on and the hull moored (a bay dock trades at once). */
export function tenderGate(cfg: Readonly<TenderConfig>, moored: boolean): boolean {
  return cfg.enabled && moored
}

/** The run's length for `tonnes` (s): `tenderS + tenderSPerKt × tonnes / 1000`, capped at `tenderCapS`. */
export function tenderDurationS(tonnes: number, cfg: Readonly<TenderConfig>): number {
  const t = tonnes > 0 ? tonnes : 0
  const s = cfg.tenderS + (cfg.tenderSPerKt * t) / 1000
  return s < cfg.tenderCapS ? s : cfg.tenderCapS
}

/** Queues a line for `station` (a cold call). Lines queued for another station are dropped first. */
export function queueTenderLine(t: TenderState, station: number, line: TenderLine): void {
  if (t.station !== station) {
    t.queued.length = 0
    t.queuedT = 0
    t.station = station
  }
  t.queued.push(line)
  t.queuedT += line.tonnes
}

/**
 * Sends every queued line as one run (a cold call). False when nothing is queued or a run is still out (the next
 * batch waits for it). Leaves the queue empty.
 */
export function sendTender(t: TenderState, cfg: Readonly<TenderConfig>): boolean {
  if (t.phase !== TENDER_PHASE.idle || t.queued.length === 0) return false
  const spare = t.sent
  spare.length = 0
  t.sent = t.queued
  t.queued = spare
  t.sentT = t.queuedT
  t.queuedT = 0
  t.runStation = t.station
  t.elapsedS = 0
  t.durationS = tenderDurationS(t.sentT, cfg)
  t.phase = TENDER_PHASE.running
  t.runs++
  return true
}

/** One step of `dt` s (allocates nothing): true on the step the run lands; the caller then settles `takeDelivered`. */
export function stepTender(t: TenderState, dt: number): boolean {
  if (t.phase !== TENDER_PHASE.running) return false
  t.elapsedS += dt
  if (t.elapsedS < t.durationS) return false
  t.elapsedS = t.durationS
  t.phase = TENDER_PHASE.landed
  return true
}

/** The landed run's lines, for the caller to settle; the tender goes idle (a cold call). Empty unless landed. */
export function takeDelivered(t: TenderState): readonly TenderLine[] {
  if (t.phase !== TENDER_PHASE.landed) return []
  const out = t.sent.slice()
  t.sent.length = 0
  t.sentT = 0
  t.phase = TENDER_PHASE.idle
  return out
}

/** Leaving the ring: the unsent lines are cancelled; a sent run keeps flying. */
export function leaveRing(t: TenderState): void {
  t.queued.length = 0
  t.queuedT = 0
  t.station = -1
}

/** The run's progress, 0..1 (0 when no run is out). */
export function tenderProgress(t: Readonly<TenderState>): number {
  if (t.phase === TENDER_PHASE.idle || t.durationS <= 0) return t.phase === TENDER_PHASE.landed ? 1 : 0
  const f = t.elapsedS / t.durationS
  return f < 1 ? f : 1
}
